import { useEffect, useRef, useCallback } from "react";
import { fmt } from "../utils/fmt";
import { NODE_TYPES } from "../utils/sector";
import { DEBRIS_ITEMS, BASE_MATERIALS } from "../data/items";

// ── Fog-of-war helpers ────────────────────────────────────────────────────────
function getVisibility(nodeId, currentNode, visitedNodes, adj) {
  if (visitedNodes.has(nodeId)) return "visited";
  const curAdj = adj.get(currentNode);
  if (curAdj && curAdj.has(nodeId)) return "adjacent";
  return "hidden";
}

// ── Visual style per node type (canvas + HUD) ─────────────────────────────────
// "color" = stroke/icon color, "fill" = node body fill, "glow" = halo rgba
const TYPE_STYLE = {
  gate_in:       { color: "#22ff88", fill: "rgba(34,255,136,0.15)",  glow: "rgba(34,255,136,0.35)"  },
  gate_out:      { color: "#ffcc00", fill: "rgba(255,204,0,0.15)",   glow: "rgba(255,204,0,0.35)"   },
  extraction:    { color: "#00cfff", fill: "rgba(0,207,255,0.12)",   glow: "rgba(0,207,255,0.28)"   },
  material_site: { color: "#dd88ff", fill: "rgba(221,136,255,0.15)", glow: "rgba(221,136,255,0.32)" },
  combat_zone:   { color: "#ff4444", fill: "rgba(255,68,68,0.15)",   glow: "rgba(255,68,68,0.35)"   },
  fuel_depot:    { color: "#ffdd33", fill: "rgba(255,221,51,0.13)",  glow: "rgba(255,221,51,0.32)"  },
  trade_outpost: { color: "#33ffcc", fill: "rgba(51,255,204,0.13)",  glow: "rgba(51,255,204,0.32)"  },
};

export function StarmapTab({
  sector, sectorIndex, currentNode, visitedNodes, discoveredMaterials = new Set(),
  smHovered, setSmHovered, handleNodeTap, confirmTravel,
  nodeActivePerks, onNodeVisited, fuel, fuelMax,
  fuelCostAdjacent = 3, fuelCostFast = 6,
  scannerCharges, onScan, fastTravelFree,
  guardianCleared, depotCooldowns = {},
}) {
  const smRef = useRef(null);
  const ssRef = useRef({ panX: 0, panY: 0, zoom: 0.6, dragging: false, lastX: 0, lastY: 0, pinchDist: 0, pulse: 0, init: false });

  const currentNodeRef   = useRef(currentNode);
  const visitedNodesRef  = useRef(visitedNodes);
  const smHoveredRef     = useRef(smHovered);
  const handleNodeTapRef = useRef(handleNodeTap);
  useEffect(() => { currentNodeRef.current   = currentNode;   }, [currentNode]);
  useEffect(() => { visitedNodesRef.current  = visitedNodes;  }, [visitedNodes]);
  useEffect(() => { smHoveredRef.current     = smHovered;     }, [smHovered]);
  useEffect(() => { handleNodeTapRef.current = handleNodeTap; }, [handleNodeTap]);
  useEffect(() => { ssRef.current.init = false; }, [sector]);

  const centerOn = useCallback(() => {
    const c = smRef.current; if (!c) return;
    const ss = ssRef.current;
    const nd = sector.nodes.find(n => n.id === currentNodeRef.current);
    if (!nd) return;
    const r = c.parentElement.getBoundingClientRect();
    ss.panX = r.width  / 2 - nd.x * ss.zoom;
    ss.panY = r.height / 2 - nd.y * ss.zoom;
  }, [sector]);

  useEffect(() => {
    const c = smRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const ss  = ssRef.current;
    let W = 0, H = 0;

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      const r   = c.parentElement.getBoundingClientRect();
      if (r.width < 10 || r.height < 10) return;
      W = r.width; H = r.height;
      c.width  = W * dpr; c.height = H * dpr;
      c.style.width  = W + "px"; c.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!ss.init) {
        const nd = sector.nodes.find(n => n.id === currentNodeRef.current);
        if (nd) { ss.panX = W / 2 - nd.x * ss.zoom; ss.panY = H / 2 - nd.y * ss.zoom; }
        ss.init = true;
      }
    }
    resize();
    window.addEventListener("resize", resize);
    const ro = new ResizeObserver(resize);
    ro.observe(c.parentElement);

    function s2w(sx, sy) { return { x: (sx - ss.panX) / ss.zoom, y: (sy - ss.panY) / ss.zoom }; }
    function findNode(sx, sy) {
      const w = s2w(sx, sy), hr = 24 / ss.zoom;
      let best = null, bestDist = Infinity;
      for (const n of sector.nodes) {
        const vis = getVisibility(n.id, currentNodeRef.current, visitedNodesRef.current, sector.adj);
        if (vis === "hidden") continue;
        const d = Math.sqrt((n.x - w.x) ** 2 + (n.y - w.y) ** 2);
        if (d < hr && d < bestDist) { best = n; bestDist = d; }
      }
      return best;
    }

    // ── Input ────────────────────────────────────────────────────────────────
    let tapStart = null, tapMoved = false;
    const oD = (e) => {
      e.preventDefault();
      const r = c.getBoundingClientRect(), t = e.touches ? e.touches[0] : e;
      const x = t.clientX - r.left, y = t.clientY - r.top;
      ss.dragging = true; ss.lastX = x; ss.lastY = y;
      tapStart = { x, y }; tapMoved = false;
      if (e.touches && e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        ss.pinchDist = Math.sqrt(dx * dx + dy * dy);
      }
    };
    const oMv = (e) => {
      e.preventDefault();
      const r = c.getBoundingClientRect();
      if (e.touches && e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const nd = Math.sqrt(dx * dx + dy * dy);
        if (ss.pinchDist > 0) {
          const sc = nd / ss.pinchDist;
          const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left;
          const my = (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top;
          const oz = ss.zoom;
          ss.zoom = Math.max(0.1, Math.min(1.5, ss.zoom * sc));
          ss.panX = mx - (mx - ss.panX) * (ss.zoom / oz);
          ss.panY = my - (my - ss.panY) * (ss.zoom / oz);
        }
        ss.pinchDist = nd; tapMoved = true; return;
      }
      const t = e.touches ? e.touches[0] : e;
      const x = t.clientX - r.left, y = t.clientY - r.top;
      if (ss.dragging) {
        const ddx = x - ss.lastX, ddy = y - ss.lastY;
        if (Math.abs(ddx) > 3 || Math.abs(ddy) > 3) tapMoved = true;
        ss.panX += ddx; ss.panY += ddy; ss.lastX = x; ss.lastY = y;
      }
    };
    const oUp = (e) => {
      e.preventDefault(); ss.dragging = false;
      if (!tapMoved && tapStart) {
        const r = c.getBoundingClientRect();
        const t = e.changedTouches ? e.changedTouches[0] : e;
        const x = t.clientX - r.left, y = t.clientY - r.top;
        const n = findNode(x, y);
        if (n) {
          const adj = sector.adj.get(currentNodeRef.current);
          const isAdjacent = adj && adj.has(n.id);
          const isVisited  = visitedNodesRef.current.has(n.id);
          // Allow selecting adjacent nodes or already-visited nodes
          if (isAdjacent || isVisited || n.id === currentNodeRef.current) {
            setSmHovered(n.id);
            handleNodeTapRef.current(n.id); // step 1: select
          }
          if (onNodeVisited && n.id !== currentNodeRef.current) onNodeVisited(n.id);
        } else { setSmHovered(null); }
      }
      tapStart = null;
    };
    const oWh = (e) => {
      e.preventDefault();
      const r = c.getBoundingClientRect(), mx = e.clientX - r.left, my = e.clientY - r.top, oz = ss.zoom;
      ss.zoom = Math.max(0.1, Math.min(1.5, ss.zoom * (e.deltaY < 0 ? 1.12 : 0.89)));
      ss.panX = mx - (mx - ss.panX) * (ss.zoom / oz);
      ss.panY = my - (my - ss.panY) * (ss.zoom / oz);
    };

    c.addEventListener("touchstart",  oD,  { passive: false });
    c.addEventListener("touchmove",   oMv, { passive: false });
    c.addEventListener("touchend",    oUp, { passive: false });
    c.addEventListener("mousedown",   oD);
    c.addEventListener("mousemove",   oMv);
    c.addEventListener("mouseup",     oUp);
    c.addEventListener("mouseleave",  () => { ss.dragging = false; });
    c.addEventListener("wheel",       oWh, { passive: false });

    const bgStars = [];
    for (let i = 0; i < 200; i++) {
      bgStars.push({ x: Math.random() * 3000 - 300, y: Math.random() * 3000 - 300, s: Math.random() * 1.4 + 0.3, b: Math.random() });
    }

    // ── Draw loop ─────────────────────────────────────────────────────────────
    let aid;
    function draw() {
      const cn = currentNodeRef.current;
      const vn = visitedNodesRef.current;
      const sh = smHoveredRef.current;
      ss.pulse += 0.025;
      if (W < 10 || H < 10) { aid = requestAnimationFrame(draw); return; }
      ctx.clearRect(0, 0, W, H);

      // Deep space background
      const bg = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, Math.max(W, H) * 0.8);
      bg.addColorStop(0, "#07101e"); bg.addColorStop(1, "#020408");
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

      ctx.save();
      ctx.translate(ss.panX, ss.panY);
      ctx.scale(ss.zoom, ss.zoom);

      // Stars (brighter than before)
      bgStars.forEach(st => {
        const a = 0.25 + Math.sin(ss.pulse * 0.3 + st.b * 10) * 0.15;
        ctx.fillStyle = `rgba(180,200,255,${a})`;
        ctx.beginPath(); ctx.arc(st.x, st.y, st.s / ss.zoom * 0.5, 0, Math.PI * 2); ctx.fill();
      });

      // Nebula blobs
      [{ x: 0.28, y: 0.35, r: 550, c: "60,20,120" }, { x: 0.72, y: 0.62, r: 450, c: "10,50,100" }, { x: 0.5, y: 0.15, r: 350, c: "0,80,60" }].forEach(nb => {
        const gd = ctx.createRadialGradient(sector.w * nb.x, sector.h * nb.y, 0, sector.w * nb.x, sector.h * nb.y, nb.r);
        gd.addColorStop(0, `rgba(${nb.c},0.08)`); gd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = gd; ctx.fillRect(0, 0, sector.w, sector.h);
      });

      const cadj = sector.adj.get(cn);

      // ── Edges ─────────────────────────────────────────────────────────────
      for (const [a, b] of sector.edges) {
        const na = sector.nodes.find(n => n.id === a);
        const nb = sector.nodes.find(n => n.id === b);
        if (!na || !nb) continue;
        const visA = getVisibility(a, cn, vn, sector.adj);
        const visB = getVisibility(b, cn, vn, sector.adj);
        if (visA === "hidden" && visB === "hidden") continue;

        const bothVisited  = vn.has(a) && vn.has(b);
        const edgeCurrent  = a === cn || b === cn;
        const eitherAdjacent = visA === "adjacent" || visB === "adjacent";

        if (edgeCurrent) {
          // Current node's edges glow with player color
          ctx.strokeStyle = "rgba(0,220,255,0.65)";
          ctx.lineWidth = 2.5; ctx.setLineDash([]);
          ctx.shadowColor = "#00ddff"; ctx.shadowBlur = 6;
        } else if (bothVisited) {
          ctx.strokeStyle = "rgba(80,160,255,0.35)";
          ctx.lineWidth = 1.2; ctx.setLineDash([]);
          ctx.shadowColor = "transparent"; ctx.shadowBlur = 0;
        } else if (eitherAdjacent) {
          ctx.strokeStyle = "rgba(255,255,255,0.14)";
          ctx.lineWidth = 0.8; ctx.setLineDash([5, 7]);
          ctx.shadowBlur = 0;
        } else { continue; }
        ctx.beginPath(); ctx.moveTo(na.x, na.y); ctx.lineTo(nb.x, nb.y); ctx.stroke();
        ctx.setLineDash([]); ctx.shadowBlur = 0;
      }

      // ── Nodes ─────────────────────────────────────────────────────────────
      for (const nd of sector.nodes) {
        const vis   = getVisibility(nd.id, cn, vn, sector.adj);
        if (vis === "hidden") continue;

        const isCur  = nd.id === cn;
        const isHov  = nd.id === sh;
        const canTr  = cadj && cadj.has(nd.id);
        const isGate = nd.type === "gate_in" || nd.type === "gate_out";
        const style  = TYPE_STYLE[nd.type] ?? TYPE_STYLE.extraction;
        const r      = isGate ? 15 : isCur ? 11 : 9;

        // Fog: adjacent-but-unvisited — mystery blob
        if (vis === "adjacent" && !isCur) {
          ctx.globalAlpha = 0.55 + Math.sin(ss.pulse * 1.8 + nd.id) * 0.1;
          ctx.fillStyle   = canTr ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.03)";
          ctx.strokeStyle = canTr ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.14)";
          ctx.lineWidth   = canTr ? 1.8 : 0.8;
          ctx.setLineDash(canTr ? [] : [3, 5]);
          ctx.beginPath(); ctx.arc(nd.x, nd.y, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
          ctx.setLineDash([]);
          if (canTr) {
            // Outer pulse ring on tappable unknown nodes
            ctx.strokeStyle = `rgba(255,255,255,${0.20 + Math.sin(ss.pulse * 3) * 0.10})`;
            ctx.lineWidth = 1.2;
            ctx.beginPath(); ctx.arc(nd.x, nd.y, r + 6 + Math.sin(ss.pulse * 3) * 2, 0, Math.PI * 2); ctx.stroke();
            ctx.fillStyle = "rgba(255,255,255,0.5)";
            ctx.font = `bold ${Math.round(10)}px sans-serif`;
            ctx.textAlign = "center"; ctx.textBaseline = "middle";
            ctx.fillText("?", nd.x, nd.y);
            ctx.textBaseline = "alphabetic";
          }
          ctx.globalAlpha = 1;
          continue;
        }

        // ── Glow halo ──────────────────────────────────────────────────────
        const glowR  = r + (isCur ? 22 : isHov ? 18 : 12) + Math.sin(ss.pulse * 2) * 3;
        const glowAlpha = isCur ? 1 : isHov ? 0.85 : 0.5;
        const gd = ctx.createRadialGradient(nd.x, nd.y, r * 0.5, nd.x, nd.y, glowR);
        // Parse color to inject opacity
        gd.addColorStop(0, style.glow.replace("0.32", String((0.32 * glowAlpha).toFixed(2))).replace("0.35", String((0.35 * glowAlpha).toFixed(2))).replace("0.28", String((0.28 * glowAlpha).toFixed(2))));
        gd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = gd; ctx.beginPath(); ctx.arc(nd.x, nd.y, glowR, 0, Math.PI * 2); ctx.fill();

        // Pulsing travel ring for reachable nodes
        if (canTr && !isCur) {
          ctx.strokeStyle = style.color + "aa";
          ctx.lineWidth = 1.8;
          ctx.shadowColor = style.color; ctx.shadowBlur = 8;
          ctx.beginPath(); ctx.arc(nd.x, nd.y, r + 6 + Math.sin(ss.pulse * 3) * 2.5, 0, Math.PI * 2); ctx.stroke();
          ctx.shadowBlur = 0;
        }

        // Selected (hovered) ring
        if (isHov && !isCur) {
          ctx.strokeStyle = "#ffffff88";
          ctx.lineWidth = 1.2; ctx.setLineDash([4, 4]);
          ctx.beginPath(); ctx.arc(nd.x, nd.y, r + 12, 0, Math.PI * 2); ctx.stroke();
          ctx.setLineDash([]);
        }

        // ── Node body ─────────────────────────────────────────────────────
        if (isGate) {
          ctx.save(); ctx.translate(nd.x, nd.y); ctx.rotate(Math.PI / 4);
          ctx.fillStyle   = nd.type === "gate_in" ? "#0b2a18" : "#2a1b00";
          ctx.strokeStyle = style.color; ctx.lineWidth = 2.5;
          ctx.shadowColor = style.color; ctx.shadowBlur = 12;
          ctx.fillRect(-r * 0.72, -r * 0.72, r * 1.44, r * 1.44);
          ctx.strokeRect(-r * 0.72, -r * 0.72, r * 1.44, r * 1.44);
          ctx.shadowBlur = 0;
          ctx.restore();
        } else {
          ctx.fillStyle   = isCur ? style.fill : canTr ? style.fill : "rgba(255,255,255,0.04)";
          ctx.strokeStyle = style.color;
          ctx.lineWidth   = isCur ? 3 : isHov ? 2.5 : canTr ? 2 : 1;
          ctx.shadowColor = style.color;
          ctx.shadowBlur  = isCur ? 18 : isHov ? 14 : canTr ? 10 : 4;
          ctx.beginPath(); ctx.arc(nd.x, nd.y, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
          ctx.shadowBlur = 0;
        }

        // Perk dots
        if (nd.perks.length > 0 && !isGate) {
          nd.perks.forEach((pk, pi) => {
            const ang = (pi / nd.perks.length) * Math.PI * 2 - Math.PI / 2, dr = r + 7;
            ctx.fillStyle   = pk.type === "buff" ? pk.color : "#ff4444";
            ctx.shadowColor = pk.type === "buff" ? pk.color : "#ff4444"; ctx.shadowBlur = 4;
            ctx.globalAlpha = 0.85 + Math.sin(ss.pulse + pi) * 0.15;
            ctx.beginPath(); ctx.arc(nd.x + Math.cos(ang) * dr, nd.y + Math.sin(ang) * dr, 3, 0, Math.PI * 2); ctx.fill();
            ctx.shadowBlur = 0; ctx.globalAlpha = 1;
          });
        }

        // Node icon / label
        ctx.textAlign = "center";
        if (isGate) {
          ctx.font = 'bold 11px system-ui, sans-serif';
          ctx.fillStyle = style.color;
          ctx.shadowColor = style.color; ctx.shadowBlur = 6;
          ctx.fillText(nd.label ?? NODE_TYPES[nd.type]?.label ?? nd.type, nd.x, nd.y - r - 9);
          ctx.shadowBlur = 0;
        } else if (isCur) {
          ctx.font = "17px sans-serif"; ctx.textBaseline = "middle";
          ctx.fillText("🚀", nd.x, nd.y);
          ctx.textBaseline = "alphabetic";
        } else {
          ctx.font = `${Math.round(11)}px sans-serif`; ctx.textBaseline = "middle";
          ctx.globalAlpha = canTr ? 1 : 0.7;
          ctx.fillText(NODE_TYPES[nd.type]?.icon ?? "◉", nd.x, nd.y);
          ctx.textBaseline = "alphabetic"; ctx.globalAlpha = 1;
        }
      }

      ctx.restore();
      aid = requestAnimationFrame(draw);
    }

    aid = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(aid); window.removeEventListener("resize", resize); ro.disconnect(); };
  // onNodeVisited and setSmHovered are stable across renders — accessed via refs inside the effect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sector]);

  // ── HUD derived values ────────────────────────────────────────────────────
  const hovNode      = smHovered !== null ? sector.nodes.find(n => n.id === smHovered) : null;
  const cAdj         = sector.adj.get(currentNode);
  const hovIsAdjacent = hovNode && cAdj && cAdj.has(hovNode.id);
  const hovIsVisited  = hovNode && visitedNodes.has(hovNode.id);
  // Can travel: adjacent (normal) OR already visited (fast travel)
  const hovCan       = hovIsAdjacent || hovIsVisited;
  const hovIsFastTravel = hovIsVisited && !hovIsAdjacent;
  const hovVis       = hovNode ? getVisibility(hovNode.id, currentNode, visitedNodes, sector.adj) : "hidden";
  const isStranded      = fuel < fuelCostAdjacent;
  const isStrandedFast  = fuel < fuelCostFast;
  const fuelPct         = fuelMax > 0 ? fuel / fuelMax : 0;
  const fuelColor       = fuelPct > 0.5 ? "#ffd54f" : fuelPct > 0.2 ? "#ff9800" : "#ff4444";
  const isFogNode       = hovVis === "adjacent" && !visitedNodes.has(hovNode?.id);

  // Label / color for the travel CTA button
  function travelLabel(node) {
    if (node.type === "gate_out") {
      if (guardianCleared) return { label: "⚡ ENTER NEXT SECTOR", color: "#ffcc00", bg: "rgba(255,200,0,0.12)" };
      return { label: "⚔ APPROACH GUARDIAN", color: "#ff8844", bg: "rgba(255,120,60,0.15)" };
    }
    const style = TYPE_STYLE[node.type] ?? TYPE_STYLE.extraction;
    if (hovIsFastTravel) {
      if (fastTravelFree)    return { label: "FAST TRAVEL  (FREE)", color: "#aaddff", bg: "rgba(100,180,255,0.10)" };
      if (isStrandedFast)    return { label: `⚠ NEED ${fuelCostFast} ⛽`, color: "#ff5544", bg: "rgba(255,50,30,0.12)" };
      return                        { label: `FAST TRAVEL  (−${fuelCostFast} ⛽)`, color: "#aaddff", bg: "rgba(100,180,255,0.10)" };
    }
    if (isStranded) return { label: `⚠ NEED ${fuelCostAdjacent} ⛽`, color: "#ff5544", bg: "rgba(255,50,30,0.12)" };
    return { label: `TRAVEL  (−${fuelCostAdjacent} ⛽)`, color: style.color, bg: style.fill };
  }

  const hasScannerCharges = scannerCharges > 0;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>
      <canvas ref={smRef} style={{ flex: 1, display: "block", touchAction: "none" }} />

      {/* Sector info — top left */}
      <div style={{ position: "absolute", top: 8, left: 8, background: "rgba(4,10,22,0.82)", borderRadius: 7, padding: "6px 10px", backdropFilter: "blur(6px)", border: "1px solid rgba(34,255,136,0.18)" }}>
        <div style={{ fontSize: 10, letterSpacing: 1, color: "#22ff88", fontWeight: 700 }}>SECTOR {sectorIndex + 1}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#55ffbb" }}>{sector.name}</div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", marginTop: 1 }}>
          {visitedNodes.size}/{sector.nodes.length} visited
        </div>
      </div>

      {/* Fuel gauge */}
      <div style={{ position: "absolute", top: 8, right: 52, background: "rgba(4,10,22,0.82)", borderRadius: 7, padding: "6px 10px", backdropFilter: "blur(6px)", border: `1px solid ${isStranded ? "rgba(255,68,68,0.45)" : "rgba(255,213,79,0.22)"}`, minWidth: 78 }}>
        <div style={{ fontSize: 10, letterSpacing: 1, color: fuelColor, fontWeight: 700, marginBottom: 3 }}>⛽ FUEL</div>
        <div style={{ height: 5, background: "rgba(255,255,255,0.07)", borderRadius: 3, overflow: "hidden", marginBottom: 3 }}>
          <div style={{ height: "100%", width: `${fuelPct * 100}%`, background: fuelColor, borderRadius: 3, transition: "width 0.3s, background 0.3s", boxShadow: `0 0 6px ${fuelColor}` }} />
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: fuelColor }}>{fmt(fuel)} / {fmt(fuelMax)}</div>
        {isStranded && <div style={{ fontSize: 9, color: "#ff6666", marginTop: 2 }}>STRANDED</div>}
      </div>

      {/* Locate button */}
      <button onClick={centerOn} style={{ position: "absolute", top: 8, right: 8, background: "rgba(4,10,22,0.82)", border: "1px solid rgba(0,200,255,0.35)", borderRadius: 6, padding: "8px 9px", color: "#33ccff", fontFamily: "inherit", fontSize: 13, cursor: "pointer", backdropFilter: "blur(6px)" }}>📍</button>

      {/* Scanner button — only shown when research unlocked */}
      {scannerCharges > 0 && (
        <button
          onClick={hasScannerCharges ? onScan : undefined}
          disabled={!hasScannerCharges}
          style={{ position: "absolute", top: 44, right: 8, background: hasScannerCharges ? "rgba(0,180,255,0.12)" : "rgba(4,10,22,0.82)", border: `1px solid ${hasScannerCharges ? "rgba(0,200,255,0.55)" : "rgba(255,255,255,0.12)"}`, borderRadius: 6, padding: "6px 9px", color: hasScannerCharges ? "#33ccff" : "rgba(255,255,255,0.3)", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: hasScannerCharges ? "pointer" : "default", backdropFilter: "blur(6px)", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
          📡 {scannerCharges}
        </button>
      )}

      {/* Active perk icons */}
      {nodeActivePerks.length > 0 && (
        <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 3 }}>
          {nodeActivePerks.map((pk, i) => (
            <div key={i} style={{ background: pk.type === "buff" ? "rgba(0,200,100,0.2)" : "rgba(220,50,50,0.2)", border: `1px solid ${pk.type === "buff" ? "rgba(0,220,120,0.35)" : "rgba(255,80,80,0.35)"}`, borderRadius: 4, padding: "2px 5px", fontSize: 13 }}>
              {pk.icon}
            </div>
          ))}
        </div>
      )}

      {/* STRANDED banner */}
      {isStranded && !hovNode && (
        <div style={{ position: "absolute", top: "38%", left: "50%", transform: "translateX(-50%)", pointerEvents: "none", textAlign: "center" }}>
          <div style={{ background: "rgba(50,0,0,0.92)", border: "1px solid rgba(255,68,68,0.5)", borderRadius: 10, padding: "14px 28px", boxShadow: "0 0 30px rgba(255,40,20,0.2)" }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>⚠️</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#ff4444", letterSpacing: 1, marginBottom: 4 }}>STRANDED</div>
            <div style={{ fontSize: 12, color: "rgba(255,180,160,0.85)", lineHeight: 1.6 }}>
              Out of fuel — craft more in the Forge<br/>or find a Fuel Depot ⛽ node
            </div>
          </div>
        </div>
      )}

      {/* Node detail panel — bottom */}
      {hovNode && hovVis !== "hidden" && (
        <div style={{ position: "absolute", bottom: 10, left: 10, right: 10, background: "rgba(4,10,22,0.94)", borderRadius: 10, padding: "12px 14px", backdropFilter: "blur(10px)", border: `1px solid ${TYPE_STYLE[hovNode.type]?.color ?? "#fff"}33` }}>

          {isFogNode ? (
            // Unknown adjacent node
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,0.5)" }}>❓ Unknown Node</span>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>#{hovNode.id}</span>
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.38)", marginBottom: 8 }}>Travel here to reveal what's inside.</div>
              {hovCan && (
                <button
                  onClick={() => !isStranded && confirmTravel(hovNode.id)}
                  disabled={isStranded}
                  style={{ width: "100%", padding: "9px 0", background: isStranded ? "rgba(100,0,0,0.2)" : "rgba(255,255,255,0.08)", border: `1px solid ${isStranded ? "rgba(255,80,60,0.35)" : "rgba(255,255,255,0.25)"}`, borderRadius: 6, color: isStranded ? "#ff5544" : "#ddeeff", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: isStranded ? "default" : "pointer", letterSpacing: 0.5 }}>
                  {isStranded ? "⚠ NO FUEL — cannot travel" : "TRAVEL  (−1 ⛽)"}
                </button>
              )}
            </>
          ) : (
            // Known node
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: TYPE_STYLE[hovNode.type]?.color ?? "#eef5ff" }}>
                  {NODE_TYPES[hovNode.type]?.icon} {NODE_TYPES[hovNode.type]?.label ?? hovNode.type}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {hovIsFastTravel && (
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.5, color: "#aaddff", background: "rgba(100,180,255,0.15)", border: "1px solid rgba(100,180,255,0.3)", borderRadius: 3, padding: "2px 5px" }}>DISCOVERED</span>
                  )}
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.35)" }}>#{hovNode.id}</span>
                </div>
              </div>

              {/* Type description */}
              {hovNode.type === "fuel_depot" && (() => {
                const cooldownUntil = depotCooldowns[hovNode.id] ?? 0;
                const msLeft = cooldownUntil - Date.now();
                const onCooldown = msLeft > 0;
                const minsLeft = Math.ceil(msLeft / 60000);
                return (
                  <div style={{ fontSize: 12, color: onCooldown ? "#ff9944" : "#ffd54f", marginBottom: 6 }}>
                    {onCooldown
                      ? `⏳ Refuel on cooldown — ${minsLeft}m remaining`
                      : "⛽ Refuels your ship by +15 on arrival"}
                  </div>
                );
              })()}
              {hovNode.type === "trade_outpost" && (
                <div style={{ fontSize: 12, color: "#33ffcc", marginBottom: 6 }}>🏪 Buy &amp; sell resources for Void-Creds</div>
              )}
              {hovNode.type === "combat_zone" && hovNode.id !== currentNode && (
                <div style={{ fontSize: 11, color: "#ff8866", background: "rgba(100,0,0,0.28)", borderRadius: 4, padding: "4px 8px", border: "1px solid rgba(255,60,50,0.22)", marginBottom: 6 }}>
                  ⚠ Enemy ships present — destroy all to clear
                </div>
              )}

              {/* Drop pool */}
              {hovNode.dropPool && hovNode.dropPool.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, letterSpacing: 1, color: "rgba(120,180,240,0.7)", fontWeight: 700, marginBottom: 4 }}>DROPS</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {hovNode.type === "material_site" && hovNode.matId && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, background: "rgba(170,85,255,0.15)", border: "1px solid rgba(170,85,255,0.35)", borderRadius: 4, padding: "2px 6px" }}>
                        {(() => {
                          const di = DEBRIS_ITEMS.find(d => d.id === hovNode.matId);
                          return di ? <><span>{di.icon}</span><span style={{ color: discoveredMaterials.has(hovNode.matId) ? "#dd88ff" : "rgba(255,255,255,0.5)" }}>{discoveredMaterials.has(hovNode.matId) ? di.name : "???"}</span></> : null;
                        })()}
                        {!discoveredMaterials.has(hovNode.matId) && <span style={{ fontSize: 10, color: "rgba(170,85,255,0.7)" }}>NEW</span>}
                      </div>
                    )}
                    {hovNode.dropPool.filter(id => id !== hovNode.matId).map(id => {
                      const di = DEBRIS_ITEMS.find(d => d.id === id);
                      return di ? (
                        <div key={id} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4, padding: "2px 6px" }}>
                          <span>{di.icon}</span>
                          <span style={{ color: di.color }}>{di.name}</span>
                        </div>
                      ) : null;
                    })}
                    {hovNode.refinedPool && hovNode.refinedPool.map(id => {
                      const bm = BASE_MATERIALS.find(m => m.id === id);
                      return bm ? (
                        <div key={id} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, background: "rgba(255,200,50,0.08)", border: "1px solid rgba(255,200,50,0.2)", borderRadius: 4, padding: "2px 6px", opacity: 0.75 }}>
                          <span>{bm.icon}</span>
                          <span style={{ color: bm.color }}>~8% {bm.name}</span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Perk list */}
              {hovNode.perks.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                  {hovNode.perks.map((pk, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                      <span>{pk.icon}</span>
                      <span style={{ color: pk.type === "buff" ? pk.color : "#ff6666", fontWeight: 700 }}>{pk.name}</span>
                      <span style={{ color: "rgba(255,255,255,0.5)" }}>{pk.desc}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginBottom: 8 }}>No node modifiers</div>
              )}

              {/* Travel CTA */}
              {hovCan && hovNode.id !== currentNode && (() => {
                const { label, color, bg } = travelLabel(hovNode);
                return (
                  <button
                    onClick={() => !isStranded && confirmTravel(hovNode.id)}
                    disabled={isStranded}
                    style={{ width: "100%", padding: "10px 0", background: bg, border: `1px solid ${color}55`, borderRadius: 7, color, fontFamily: "inherit", fontSize: 13, fontWeight: 800, cursor: isStranded ? "default" : "pointer", letterSpacing: 0.5, boxShadow: isStranded ? "none" : `0 0 10px ${color}33` }}>
                    {label}
                  </button>
                );
              })()}
              {!hovCan && hovNode.id !== currentNode && (
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", textAlign: "center" }}>Not reachable — explore adjacent nodes first</div>
              )}
              {hovNode.id === currentNode && (
                <div style={{ fontSize: 12, fontWeight: 600, color: "#33ccff", textAlign: "center" }}>📍 You are here</div>
              )}
            </>
          )}
        </div>
      )}

      {/* Legend — bottom-right when nothing hovered */}
      {!hovNode && (
        <div style={{ position: "absolute", bottom: 10, right: 10, background: "rgba(4,10,22,0.82)", borderRadius: 7, padding: "7px 10px", backdropFilter: "blur(4px)", border: "1px solid rgba(255,255,255,0.07)" }}>
          {Object.entries(NODE_TYPES).filter(([k]) => k !== "gate_in" && k !== "gate_out").map(([key, val]) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3, fontSize: 11 }}>
              <span>{val.icon}</span>
              <span style={{ color: TYPE_STYLE[key]?.color ?? "#fff", fontWeight: 600 }}>{val.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
